import { useQuery } from "@tanstack/react-query";
import { getItems } from "../../../api/items";
import { DateRange } from "../../../dashboard-framework/types";
import { fetchAllItemPages } from "./fetchAllItemPages";
import {
    brainMapsFilter,
    doneClientSessionsFilter,
    DONE_SESSIONS_FIELDS,
    EVALUATIONS_FIELDS,
    internalSessionsFilter,
    INTERNAL_SESSIONS_FIELDS,
    PEOPLE_FIELDS,
    PEOPLE_LOOKUP_CHUNK_SIZE,
    schedulesFilter,
    SCHEDULES_FIELDS,
    TECHNICIANS_FIELDS,
    widenRangeForFetch,
} from "./neurofeedbackProductivityConfig";
import { collectActiveUserIds, neurofeedbackProductivityRows, PersonInfo, specializationsByUserIdOf } from "./neurofeedbackProductivityRows";

const FIVE_MINUTES = 5 * 60 * 1000;

// Universos acima disto (15 páginas × 1000) disparam o aviso de período demasiado longo.
const NFB_MAX_PAGES = 15;

// /users ignora limit/page (createRoute) e responde com ≤100 — lookup em blocos de ids.
const fetchPeopleByIds = async (personIds: Array<string>) => {
    const idChunks: Array<Array<string>> = [];
    for (let chunkStart = 0; chunkStart < personIds.length; chunkStart += PEOPLE_LOOKUP_CHUNK_SIZE) {
        idChunks.push(personIds.slice(chunkStart, chunkStart + PEOPLE_LOOKUP_CHUNK_SIZE));
    }
    const chunkResults = await Promise.all(
        idChunks.map((idChunk) => getItems("directus_users", PEOPLE_FIELDS, { filter: { id: { _in: idChunk } }, sort: "first_name" }))
    );
    const people = chunkResults.flatMap((chunkResult) => chunkResult.items);
    return people;
};

type NeurofeedbackProductivityQuery = {
    periodRange: DateRange;
    restrictToUserId: string | null;
    enabled: boolean;
};

export const useNeurofeedbackProductivityRows = ({ periodRange, restrictToUserId, enabled }: NeurofeedbackProductivityQuery) => {
    const fetchRange = widenRangeForFetch(periodRange);

    // Técnicos servem para o mapa de especializações (classificação por função) E como universo:
    // só quem tem ficha de técnico entra (as chaves do mapa). O que aparece na tabela é ainda
    // recortado por atividade no período.
    const techniciansResult = useQuery({
        queryKey: ["nfb-productivity-technicians"],
        queryFn: () => fetchAllItemPages("technicians", TECHNICIANS_FIELDS, {}, { maxPages: 2 }),
        staleTime: FIVE_MINUTES,
        keepPreviousData: true,
    });
    const technicians = techniciansResult.data?.items || [];
    const specializationsByUserId = specializationsByUserIdOf(technicians);

    const schedulesResult = useQuery({
        queryKey: ["nfb-productivity-schedules", JSON.stringify({ fetchRange, restrictToUserId })],
        queryFn: () => fetchAllItemPages("schedules", SCHEDULES_FIELDS, schedulesFilter(fetchRange, restrictToUserId), { maxPages: NFB_MAX_PAGES }),
        staleTime: FIVE_MINUTES,
        keepPreviousData: true,
        enabled,
    });
    const doneSessionsResult = useQuery({
        queryKey: ["nfb-productivity-done-sessions", JSON.stringify({ fetchRange, restrictToUserId })],
        queryFn: () =>
            fetchAllItemPages("sessions", DONE_SESSIONS_FIELDS, doneClientSessionsFilter(fetchRange, restrictToUserId), { maxPages: NFB_MAX_PAGES }),
        staleTime: FIVE_MINUTES,
        keepPreviousData: true,
        enabled,
    });
    const internalSessionsResult = useQuery({
        queryKey: ["nfb-productivity-internal-sessions", JSON.stringify(fetchRange)],
        queryFn: () => fetchAllItemPages("sessions", INTERNAL_SESSIONS_FIELDS, internalSessionsFilter(fetchRange), { maxPages: NFB_MAX_PAGES }),
        staleTime: FIVE_MINUTES,
        keepPreviousData: true,
        enabled,
    });
    const evaluationsResult = useQuery({
        queryKey: ["nfb-productivity-evaluations", JSON.stringify({ fetchRange, restrictToUserId })],
        queryFn: () => fetchAllItemPages("evaluations", EVALUATIONS_FIELDS, brainMapsFilter(fetchRange, restrictToUserId), { maxPages: NFB_MAX_PAGES }),
        staleTime: FIVE_MINUTES,
        keepPreviousData: true,
        enabled,
    });

    const rawData = {
        schedules: schedulesResult.data?.items || [],
        doneSessions: doneSessionsResult.data?.items || [],
        internalSessions: internalSessionsResult.data?.items || [],
        evaluations: evaluationsResult.data?.items || [],
    };
    // Só precisamos dos nomes/role dos técnicos que aparecem nos dados (o universo já é técnicos-only).
    const universeUserIds = collectActiveUserIds(rawData).filter((userId) => userId in specializationsByUserId);

    const peopleResult = useQuery({
        queryKey: ["nfb-productivity-people", universeUserIds.slice().sort().join(",")],
        queryFn: () => fetchPeopleByIds(universeUserIds),
        staleTime: FIVE_MINUTES,
        enabled: enabled && universeUserIds.length > 0,
    });
    const peopleById: Record<string, PersonInfo> = {};
    const people: Array<PersonInfo> = Array.isArray(peopleResult.data) ? peopleResult.data : [];
    people.forEach((person) => {
        peopleById[person.id] = person;
    });

    const inputs = { ...rawData, peopleById, specializationsByUserId };
    const allRows = enabled ? neurofeedbackProductivityRows(inputs, periodRange) : [];
    const rows = restrictToUserId ? allRows.filter((row) => row.userId === restrictToUserId) : allRows;

    const reachedPageLimit = Boolean(
        schedulesResult.data?.reachedPageLimit ||
            doneSessionsResult.data?.reachedPageLimit ||
            internalSessionsResult.data?.reachedPageLimit ||
            evaluationsResult.data?.reachedPageLimit
    );
    const isLoading =
        techniciansResult.isLoading ||
        (enabled && (schedulesResult.isLoading || doneSessionsResult.isLoading || internalSessionsResult.isLoading || evaluationsResult.isLoading)) ||
        (enabled && universeUserIds.length > 0 && peopleResult.isLoading);
    const isError =
        techniciansResult.isError ||
        schedulesResult.isError ||
        doneSessionsResult.isError ||
        internalSessionsResult.isError ||
        evaluationsResult.isError ||
        peopleResult.isError;

    const productivity = { rows, specializationsByUserId, fetchRange, reachedPageLimit, isLoading, isError };
    return productivity;
};
